import { Button } from '@/components/ui/button';
import { AiChat, ChatMenu, type AiChatItem } from '@common-components/ai-chat';
import { Mascot } from '@common-components/mascot/Mascot';
import { namespace } from '@common-config/constants';
import { backendApi, MyBridgeAgent } from '@common/reducers/backend-api/backend-api';
import { Check, Columns2, Loader2, MessageSquare, RotateCcw, SlidersHorizontal } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHideCrisp } from '@common/hooks/use-hide-crisp';
import { AgentForm } from './AgentForm';
import { McpConnectCard } from './McpServersSection';
import { AgentFace } from '@common-components/mascot/AgentFace';
import { SegmentedControl } from './SegmentedControl';
import { BuilderChatContext, READY_ITEM_ID, useBuilderChat } from './useBuilderChat';
import { resolveLayout, selectedOption, viewAfterCreate, viewOptions, WorkspaceView } from './workspace-view';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { isUserWorkSpaceOwner, selectCurrentWorkspace } from '@common/reducers/UserSelectors';
import { reportAgentEvent } from '@common/services/agent-analytics';
import { BuilderAskCard } from './BuilderAskCard';
import { askReplyText, isAskActive } from './builder-ask';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/**
 * THE agent workspace — one layout for New and Edit, on the web page and in the
 * extension dialog: the AI builder chat and the agent form, as chat only, form
 * only, or both side by side (`workspace-view.ts` has the rules).
 *
 * The frame never scrolls; each pane scrolls on its own. Both panes stay mounted
 * whichever is showing, so switching views never loses a draft.
 *
 * Create → edit continuity: the builder conversation lives in `useBuilderChat`,
 * inside this component. When a build creates the agent the host re-points the
 * workspace at it (the page replaces the URL; the dialog swaps its state) while
 * this component stays mounted, so the conversation stays on screen and the
 * "Ready" card lands under it.
 *
 * The agent, the account's abilities (can build, admin) and the agent's policy and
 * schedules come from the overview query (RTK cache), so a host passes only which
 * agent, the view and its own callbacks.
 */
export interface AgentWorkspaceProps {
  /** The agent the workspace edits; null/undefined with no `agentId` = a new agent. */
  agentId?: string | null;
  /** The requested view (`?view=`); null = the mode's default. */
  view: WorkspaceView | null;
  onViewChange: (view: WorkspaceView) => void;
  /** An agent was created (by the chat or the form): point the workspace at it. */
  onCreated: (agentId: string, view: WorkspaceView) => void;
  /** A save of an existing agent succeeded. */
  onSaved?: () => void;
  /** Start of the header bar (a back link, an "open in new tab" link). */
  headerStart?: React.ReactNode;
  /** End of the header bar (a dialog's close button). */
  headerEnd?: React.ReactNode;
  /** The frame's own width cap in px (the extension dialog); the page uses the window. */
  maxWidth?: number;
  /** Hosted in the extension dialog: the ⋯ sits beside the host's close button; no Crisp corner clearance. */
  inDialog?: boolean;
  /** Bind a NEW agent to one WhatsApp chat (Quick Agents in WhatsApp Web). */
  chatContext?: BuilderChatContext;
  isRtl?: boolean;
}

/** The window width, live. */
function useWindowWidth(): number {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const VIEW_ICONS: Record<WorkspaceView, React.ComponentType<{ className?: string }>> = {
  chat: MessageSquare,
  form: SlidersHorizontal,
  both: Columns2,
};

/**
 * The created agent, under the builder's last message. It is created paused, so
 * the next step is to turn it on; once it is on, the card says it is live. "Show
 * settings" appears only when the form is not already on screen.
 */
const AgentReadyCard: React.FC<{ agent: MyBridgeAgent; enabled: boolean; onShowSettings?: () => void }> = ({
  agent,
  enabled,
  onShowSettings,
}) => {
  const { t } = useTranslation(namespace);
  const [updateAgent, { isLoading }] = backendApi.useUpdateMyBridgeAgentMutation();
  return (
    <div
      className="self-start flex flex-wrap items-center gap-x-4 gap-y-3 rounded-ai-card border border-ai-border bg-ai-surface px-4 py-3.5 max-w-[520px] animate-in fade-in slide-in-from-bottom-1 duration-(--ai-duration-enter) ease-ai"
      data-testid="agent-ready-card"
      data-enabled={enabled}
    >
      <AgentFace agent={agent} size={44} mood="happy" />
      <div className="min-w-0 flex-1 basis-40">
        <div className="text-sm font-semibold break-words text-ai-text" dir="auto" style={{ unicodeBidi: 'plaintext' }}>
          {agent.label}
        </div>
        {enabled ? (
          <div className="inline-flex items-center gap-1 text-xs font-medium text-ai-text" data-testid="agent-ready-live">
            <Check className="h-3.5 w-3.5" /> {t('myBridge.workspace.readyLive')}
          </div>
        ) : (
          <div className="text-xs text-ai-text-secondary">{t('myBridge.workspace.readyBody')}</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {onShowSettings && (
          <Button size="sm" variant="ghost" className="text-ai-text-secondary" onClick={onShowSettings} data-testid="agent-ready-settings">
            {t('myBridge.workspace.readyShowSettings')}
          </Button>
        )}
        {!enabled && (
          <Button
            size="sm"
            variant="ai"
            disabled={isLoading}
            onClick={() => void updateAgent({ id: agent.id, enabled: true }).unwrap()
              .then(() => reportAgentEvent('agent_activated', 'claude', { agentId: agent.id }))
              .catch(() => undefined)}
            data-testid="agent-ready-turn-on"
          >
            {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('myBridge.workspace.readyTurnOn')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const AgentWorkspace: React.FC<AgentWorkspaceProps> = ({
  agentId,
  view,
  onViewChange,
  onCreated,
  onSaved,
  headerStart,
  headerEnd,
  maxWidth,
  inDialog,
  chatContext,
  isRtl,
}) => {
  const { t } = useTranslation(namespace);
  // null until the user's current workspace has loaded: ownership is unknown, not "no".
  const isOwner = useAppSelector(isUserWorkSpaceOwner);
  const workspaceLoaded = useAppSelector((state) => !!selectCurrentWorkspace(state));
  const canBuyCredits = workspaceLoaded ? isOwner : null;
  const { data: overview } = backendApi.useGetMyBridgeOverviewQuery();
  const agent = agentId ? overview?.agents?.find((a) => a.id === agentId) ?? null : null;
  const hasAgents = (overview?.agents?.length ?? 0) > 0;
  // rev 3.3 §6.17.1: a configured + provisioned account can build (unknown while loading).
  const canCreate = !overview || (!!overview.configured && !!overview.provisioned);
  const disabledReason = canCreate ? undefined : t('myBridge.hero.unavailable');
  const policy = agentId ? overview?.policies?.[agentId] : undefined;
  const mode = agentId ? 'edit' : 'new';
  const windowWidth = useWindowWidth();
  const layout = resolveLayout(mode, view, Math.min(windowWidth, maxWidth ?? Infinity));
  // Both panes fill the bottom corners (composer + Save): no Crisp in the split view.
  useHideCrisp(!inDialog && layout.split);

  const chat = useBuilderChat({
    agentId,
    chatContext,
    disabled: !canCreate && !agentId,
    onCreated: (created) => onCreated(created.id, viewAfterCreate(layout.offerBoth)),
  });
  // A just-created agent is on screen before the overview lists it.
  const current = agent ?? (chat.readyAgent && chat.readyAgent.id === agentId ? chat.readyAgent : null);

  const dir = isRtl ? 'rtl' : 'ltr';
  const showChat = layout.split || layout.pane === 'chat';
  const showForm = layout.split || layout.pane === 'form';

  const empty = mode === 'new' ? (
    <div className="flex flex-col items-center" data-testid="agents-hero">
      <Mascot size={layout.mobile ? 72 : 116} glow level="full" />
      <h2 className="mt-3 text-3xl sm:text-[44px] sm:leading-[1.05] font-semibold tracking-[-0.035em] text-ai-text">
        {hasAgents ? t('myBridge.hero.titleStartMore') : t('myBridge.hero.titleStart')}{' '}
        <span
          className="whitespace-nowrap bg-clip-text text-transparent"
          style={{ backgroundImage: 'linear-gradient(90deg, #3b82f6, #6366f1)' }}
        >
          {t('myBridge.hero.titleAccent')}
        </span>
      </h2>
      <p className="mt-3 max-w-2xl text-base leading-relaxed text-ai-text-secondary">
        {t('myBridge.hero.subtitle')}
        <GuideVideoButton lesson="build-an-ai-agent" variant="link" surface="agents_new_hero" style={{ marginInlineStart: 6, fontSize: 'inherit', fontWeight: 'inherit', height: 'auto' }} />
      </p>
      {!canCreate && disabledReason && <p className="mt-4 text-sm text-ai-text-secondary">{disabledReason}</p>}
    </div>
  ) : (
    <div className="flex flex-col items-center">
      <Mascot size={64} glow />
      <div className="mt-5 text-base font-semibold tracking-tight text-ai-text">{t('myBridge.builderChat.emptyTitle')}</div>
      <p className="mt-1.5 max-w-xs text-xs leading-5 text-ai-text-secondary">{t('myBridge.builderChat.empty')}</p>
      {chat.previous && (
        <div className="mt-4 w-full max-w-sm text-start" data-testid="builder-previous-chat">
          <div className="text-xs font-medium text-ai-text-secondary">
            {t('myBridge.builderChat.previousTitle', { defaultValue: 'Earlier conversation' })}
          </div>
          <ul className="mt-1.5 max-h-44 overflow-y-auto rounded-ai-md border border-ai-border p-2 space-y-1.5 text-xs text-ai-text-secondary" data-testid="builder-previous-items">
            {chat.previous.groups.map((group, gi) => (
              <React.Fragment key={group.conversationId}>
                {gi > 0 && <li role="separator" className="border-t border-ai-border" data-testid="builder-previous-separator" />}
                {group.items.map((item) => (
                  item.kind === 'user' || item.kind === 'assistant' ? (
                    <li key={item.id} className="whitespace-pre-wrap" dir="auto">
                      <span className="font-medium text-ai-text">{item.kind === 'user' ? t('myBridge.builderChat.previousYou', { defaultValue: 'You' }) : t('myBridge.builderChat.previousAi', { defaultValue: 'AI' })}: </span>
                      {item.text}
                    </li>
                  ) : null
                ))}
              </React.Fragment>
            ))}
          </ul>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); chat.previous?.resume(); }}
            className="mt-2 inline-flex items-center gap-1 text-xs rounded-ai-pill px-2.5 py-1.5 bg-ai-muted text-ai-text hover:opacity-80"
            data-testid="builder-previous-continue"
          >
            {t('myBridge.builderChat.continuePrevious', { defaultValue: 'Continue this conversation' })}
          </button>
        </div>
      )}
    </div>
  );

  const suggestions = mode === 'new'
    ? [1, 2, 3, 4, 5, 6, 7].map((n) => t(`myBridge.hero.suggestion${n}`))
    : [t('myBridge.builderChat.suggestion1'), t('myBridge.builderChat.suggestion2'), t('myBridge.builderChat.suggestion3')];

  const placeholder = chat.unavailable
    ? (mode === 'new' ? disabledReason ?? t('myBridge.builderChat.unavailable') : t('myBridge.builderChat.unavailable'))
    : mode === 'edit'
      ? t('myBridge.builderChat.placeholder')
      : chat.items.length > 0
        ? t('myBridge.builder.placeholderFollowUp')
        : chatContext
          ? t('myBridge.builder.placeholderChat')
          : layout.mobile
            ? t('myBridge.hero.placeholderShort')
            : t('myBridge.hero.placeholder');

  // One composer look with btgpt: no key hint; only "Start over" (new build) / "New chat"
  // (edit, F6: every Edit chat is its own builder conversation) once the chat has begun.
  const composerFooter = chat.items.length > 0 && !chat.working ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); chat.reset(); }}
      className="inline-flex items-center gap-1 text-xs rounded-ai-pill px-2.5 py-1.5 text-ai-text-secondary hover:bg-ai-muted"
      data-testid="builder-chat-reset"
    >
      <RotateCcw className="h-3 w-3" /> {mode === 'new' ? t('myBridge.builder.startOver') : t('myBridge.builderChat.newChat')}
    </button>
  ) : undefined;

  // The agent's outside apps that still need the user (the builder added them from the prompt):
  // a Connect card under the LAST message, once the turn is done.
  const lastItemId = chat.items[chat.items.length - 1]?.id;
  const connectCardAgentId = !chat.working ? current?.id : undefined;
  const renderExtra = (item: AiChatItem) => {
    const readyAgent = item.id === READY_ITEM_ID ? chat.readyAgent : null;
    const connectCard = item.id === lastItemId && connectCardAgentId;
    // #813 step 4 — the builder asked "update the existing agent, or create a new one?": two buttons until answered.
    const askCard = item.kind === 'assistant' && item.ask && isAskActive(chat.items, item.id) ? item.ask : null;
    if (!readyAgent && !connectCard && !askCard) return null;
    return (
      <>
        {askCard && <BuilderAskCard ask={askCard} disabled={chat.working} onChoose={(choice) => void chat.send(askReplyText(askCard, choice, t))} />}
        {readyAgent && (
          <AgentReadyCard
            agent={readyAgent}
            enabled={!!(current?.id === readyAgent.id ? current.enabled : readyAgent.enabled)}
            onShowSettings={showForm ? undefined : () => onViewChange(layout.offerBoth ? 'both' : 'form')}
          />
        )}
        {connectCard && <McpConnectCard agentId={connectCard} />}
      </>
    );
  };

  const title = current?.label ?? (mode === 'new' ? t('myBridge.workspace.newTitle') : '');

  return (
    <div className="flex flex-col h-full min-h-0 bg-ai-bg" dir={dir} data-testid="agent-workspace" data-view={selectedOption(layout)} data-mode={mode}>
      <header className="shrink-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 h-14">
        {headerStart}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {current && <AgentFace agent={current} size={28} />}
          <h1
            className="text-base font-semibold truncate text-ai-text"
            dir="auto"
            style={{ unicodeBidi: 'plaintext' }}
            data-testid="agent-workspace-title"
          >
            {title}
          </h1>
          {mode !== 'new' && <GuideVideoButton lesson="agent-settings" variant="icon" surface="agent_workspace_header" />}
        </div>
        <SegmentedControl<WorkspaceView>
          value={selectedOption(layout)}
          onChange={onViewChange}
          ariaLabel={t('myBridge.workspace.viewSwitch')}
          testId="workspace-view"
          options={viewOptions(layout.offerBoth).map((v) => ({
            value: v,
            label: t(`myBridge.workspace.view_${v}`),
            icon: VIEW_ICONS[v],
          }))}
        />
        {headerEnd}
      </header>

      <div className="flex-1 min-h-0 flex">
        <section
          className={showChat ? `flex-1 min-w-0 flex flex-col ${layout.split ? 'min-w-[400px]' : ''}` : 'hidden'}
          aria-label={t('myBridge.workspace.view_chat')}
          data-testid="workspace-chat-pane"
        >
          <AiChat
            items={chat.items}
            canBuyCredits={canBuyCredits}
            onSend={chat.send}
            attachments={{ upload: chat.upload }}
            busy={chat.working}
            onStop={chat.stop}
            stream={chat.stream}
            empty={empty}
            suggestions={suggestions}
            renderExtra={renderExtra}
            // "Show while it works" is about the chat, so its ⋯ sits in the chat's own composer.
            composer={{ placeholder, disabled: chat.unavailable, leading: <ChatMenu dir={dir} align="start" testId="workspace-menu" />, footer: composerFooter }}
            // 'lg' everywhere: in the split the chat now has the room for a page column.
            size="lg"
            dir={dir}
            // Chat alone: the composer reaches Crisp's corner. Split: Crisp is hidden. The dialog has no Crisp.
            cornerClearance={!inDialog && !layout.split}
          />
        </section>
        <section
          className={showForm ? `min-w-0 flex flex-col ${layout.split ? 'flex-[0_1_720px] max-w-[60%] bg-ai-surface rounded-ss-[28px]' : 'flex-1'}` : 'hidden'}
          aria-label={t('myBridge.workspace.view_form')}
          data-testid="workspace-form-pane"
        >
          {mode === 'edit' && !current ? (
            // A just-created agent whose data is still on its way: never offer "Create" for it.
            <div className="flex-1 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-ai-text-secondary" /></div>
          ) : (
            <AgentForm
              agent={current}
              isRtl={isRtl}
              isAdmin={overview?.isAdmin}
              policy={policy}
              schedules={overview?.schedules ?? []}
              card={!layout.split}
              onSaved={(savedId, created) => (created ? onCreated(savedId, view ?? 'form') : onSaved?.())}
            />
          )}
        </section>
      </div>
    </div>
  );
};
